import React, { useCallback, useEffect, useMemo } from 'react'
import { Box, Carousel, Skeleton, Container, Image, CarouselButton, Link } from '@thirstycamel/ui'
import useMedia from 'use-media'
import dayjs from 'dayjs'
import { useQuery } from 'react-query'

import { useStore } from '../../store/hooks'
import imageProxy from '../../utils/imageProxy'
import { backend } from '../../utils/backend'
import { fetchGraphQL } from '../../utils/graphql-fetch'
import { eventGA4, eventUA } from '../../utils/gtag'
import { useGoogleAnalytics } from '../GoogleAnalyticsProvider'
import { useApp } from '../../hooks/useApp'
import { cmsUseNew } from '../../utils/cms'
import { Banner, BannersDocument } from '../../../generated'

const AUTOPLAY = 1000 * 7 // 7 seconds
const BANNERS_FETCH_LIMIT = 20
const BANNERS_SHOWING_LIMIT = 10

/* The banner shape returned by the core `/banners/public` endpoint. */
interface PublicBanner {
  id: string
  title: string
  link?: string
  isLinkExternal?: boolean
  imageDesktop?: { filename: string }
  imageMobile?: { filename: string }
}

/* This algorithm is not really that random, nor does it work well on larger arrays - it
 * should be fine for this use-case, however. */
const handleSortRandom = () => {
  return 0.5 - Math.random()
}

const HomeBannerCore = () => {
  const isMobile = useMedia({ maxWidth: '30em' })
  const { isMobileApp } = useApp()
  const { trackEvent } = useGoogleAnalytics()

  const location = useStore(s => s.location)
  const selectedStore = useStore(s => s.store.selectedStore)
  const isDelivery = useStore(store => store.cart?.cart?.type === 'TAH')

  const region = selectedStore?.region || location.region || 'VIC'
  const deliveryType = isDelivery ? 'DELIVERY' : 'CLICK_AND_COLLECT'
  const storeCode = selectedStore?.humpClubStoreCode || selectedStore?.slug

  /* Server-side filtering (active window, region, delivery type, store) is handled by the
   * endpoint; the client just shuffles and caps the result for the carousel. */
  const query = useQuery<PublicBanner[]>(
    ['banners/public', 'HOME_CAROUSEL', region, deliveryType, storeCode],
    () =>
      backend<PublicBanner[]>('banners/public', {
        params: { placement: 'HOME_CAROUSEL', region, deliveryType, storeCode },
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 30000,
    },
  )

  /* Determine whether to use the desktop or mobile image for the banners. */
  const imageKey = isMobile ? 'imageMobile' : 'imageDesktop'

  const banners: PublicBanner[] = useMemo(
    () =>
      query.data ? [...query.data].sort(handleSortRandom).slice(0, BANNERS_SHOWING_LIMIT) : [],
    [query.data],
  )

  useEffect(() => {
    if (!banners?.length) return

    banners.forEach((banner, index) => {
      trackEvent('ec', 'addPromo', {
        name: banner.title,
        id: banner.id,
        creative: 'banner',
        position: `banner_slot${index}`,
        non_interaction: true,
      })
    })

    /* Send to Google Analytics */
    eventUA('view_promotion', {
      event_label: 'Shown homepage banner',
      non_interaction: true,
      event_category: 'ecommerce',
      promotions: banners.map(banner => ({
        name: banner.title,
        id: banner.id,
        creative_slot: 'banner',
      })),
    })

    // Send to GA4
    banners.forEach(banner =>
      eventGA4('view_promotion', {
        creative_slot: 'banner',
        promotion_id: banner.id,
        promotion_name: banner.title,
      }),
    )
  }, [banners, trackEvent])

  /* If there are no banners, and we aren't fetching, show nothing. */
  if (!query.isFetching && !banners?.length) return null

  const handleClickBanner = (banner: PublicBanner) => {
    trackEvent('ec', 'setAction', 'promo_click')
    trackEvent('send', 'event', 'Internal Promotions', 'click', banner.title)

    /* Send to Google Analytics */
    eventUA('select_promotion', {
      event_category: 'ecommerce',
      event_label: 'Clicked on homepage banner',
      promotions: [{ name: banner.title, id: banner.id, creative_slot: 'banner' }],
    })

    eventGA4('select_promotion', {
      creative_slot: 'banner',
      promotion_name: banner.title,
      promotion_id: banner.id,
    })
  }

  const resolveLink = (banner: PublicBanner) => {
    const isLinkExternal = isMobileApp
      ? banner.link?.startsWith?.('http') &&
        !banner.link?.startsWith?.('https://thirstycamel.com.au') &&
        !banner.link?.startsWith?.('https://www.thirstycamel.com.au')
      : banner.isLinkExternal || banner.link?.startsWith?.('http')

    const url = isLinkExternal
      ? banner.link
      : banner.link
          ?.replace('https://thirstycamel.com.au', '')
          ?.replace('https://www.thirstycamel.com.au', '')

    return { isLinkExternal, url }
  }

  return (
    <Box bg="gray.50" borderBottom="1px" borderColor="gray.200">
      <Container px={[0, , 12]} size="lg" display="flex" pt={[, , 10]} pb={[0, , 12]}>
        <Box
          h="100%"
          w="100%"
          overflow={['hidden', , 'initial']}
          paddingBottom={['calc(200 / 300 * 100%)', 'calc(400 / 1400 * 100%)']}
          position="relative"
          border="1px solid"
          borderColor="gray.200"
        >
          <Box position="absolute" h="100%" w="100%">
            {query.isFetching && !banners?.length ? (
              <Skeleton h="100%" w="100%" />
            ) : banners?.length === 1 ? (
              banners.map(banner => {
                const image = (
                  <Image
                    src={imageProxy(banner[imageKey]?.filename)}
                    objectFit="cover"
                    w="100%"
                    h="100%"
                  />
                )

                if (!banner.link) return image

                const { isLinkExternal, url } = resolveLink(banner)

                return (
                  <Link
                    key={banner.id}
                    href={url}
                    isExternal={isLinkExternal}
                    h="100%"
                    w="100%"
                    display="block"
                  >
                    {image}
                  </Link>
                )
              })
            ) : (
              <Carousel
                showDots
                arrows
                arrowLeft={<CarouselButton side="left" />}
                arrowRight={<CarouselButton side="right" />}
                infinite
                autoPlay={AUTOPLAY}
              >
                {banners.map(banner => {
                  const image = (
                    <Image
                      key={banner.title}
                      src={imageProxy(banner[imageKey]?.filename)}
                      objectFit="cover"
                      w="100%"
                      h="100%"
                    />
                  )

                  if (!banner.link) return image

                  const { isLinkExternal, url } = resolveLink(banner)

                  return (
                    <Link
                      key={banner.id}
                      href={url}
                      isExternal={isLinkExternal}
                      h="100%"
                      w="100%"
                      display="block"
                      onClick={() => handleClickBanner(banner)}
                    >
                      {image}
                    </Link>
                  )
                })}
              </Carousel>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

const HomeBannerWordPress = () => {
  const isMobile = useMedia({ maxWidth: '30em' })
  const { isMobileApp } = useApp()
  const { trackEvent } = useGoogleAnalytics()

  const location = useStore(s => s.location)
  const selectedStore = useStore(s => s.store.selectedStore)
  const isDelivery = useStore(store => store.cart?.cart?.type === 'TAH')

  let region = selectedStore?.region || location.region || 'VIC'

  /* If region is NSWBR then change it to NSW_BR as this is how the CMS is setup */
  if (region === 'NSWBR') {
    region = 'NSW_BR'
  }

  const bannerType = isDelivery ? 'DELIVERY' : 'CLICK_AND_COLLECT'

  const query = useQuery<any, any>(
    ['api/cms', BannersDocument, { first: BANNERS_FETCH_LIMIT, region, type: bannerType }],
    fetchGraphQL,
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 30000,
    },
  )

  /* Determine whether to use the desktop or mobile image for the banners. */
  const imageKey = isMobile ? 'imageMobile' : 'imageDesktop'

  const handleFilter = useCallback(
    (banner: { node: Banner }) => {
      if (!banner?.node) return false

      /* Check if the banner is the correct type */
      if (banner.node.acf?.type?.length) {
        const isCorrectType = banner.node.acf.type.includes(bannerType)

        if (!isCorrectType) return false
      }

      /* Check if the banner has expired */
      if (banner.node.acf?.endsAt) {
        /* Set the `endsAt` date (which is just MM/DD/YYYY) to 11:59pm in AEST (+10). */
        const endsAt = dayjs(`${banner.node.acf.endsAt} 23:59:59 +1000`)
        const isExpired = dayjs(endsAt).isBefore(Date.now())

        if (isExpired) return false
      }

      /* Check that the banner is valid for the region */
      if (banner.node.acf?.region?.length) {
        const isCorrectRegion = banner.node.acf.region.includes(region)

        if (!isCorrectRegion) return false
      }

      /* Check that the banner has a store code */
      if (banner.node.acf?.storeCode) {
        const storeCodes = banner?.node?.acf?.storeCode?.split(',')
        const isCorrectStore = selectedStore?.humpClubStoreCode
          ? storeCodes?.includes(selectedStore?.humpClubStoreCode)
          : storeCodes?.includes(selectedStore?.slug)

        if (!isCorrectStore) return false
      }

      return true
    },
    [bannerType, region, selectedStore],
  )

  const banners: { node: Banner }[] = useMemo(
    () =>
      query?.data?.banners?.edges
        ?.sort?.(handleSortRandom)
        .filter(handleFilter)
        .slice(0, BANNERS_SHOWING_LIMIT),
    [query?.data?.banners?.edges, handleFilter],
  )

  useEffect(() => {
    if (!banners?.length) return

    banners.forEach(({ node: banner }, index) => {
      trackEvent('ec', 'addPromo', {
        name: banner.title,
        id: banner.id,
        creative: 'banner',
        position: `banner_slot${index}`,
        non_interaction: true,
      })
    })

    /* Send to Google Analytics */
    eventUA('view_promotion', {
      event_label: 'Shown homepage banner',
      non_interaction: true,
      event_category: 'ecommerce',
      promotions: banners.map(({ node: banner }) => ({
        name: banner.title,
        id: banner.id,
        creative_slot: 'banner',
      })),
    })

    // Send to GA4
    banners.forEach(({ node: banner }) =>
      eventGA4('view_promotion', {
        creative_slot: 'banner',
        promotion_id: banner.id,
        promotion_name: banner.title,
      }),
    )
  }, [banners, trackEvent])

  /* If there are no banners, and we aren't fetching, show nothing. */
  if (!query.isFetching && !banners?.length) return null

  const handleClickBanner = (banner: Banner) => {
    trackEvent('ec', 'setAction', 'promo_click')
    trackEvent('send', 'event', 'Internal Promotions', 'click', banner.title)

    /* Send to Google Analytics */
    eventUA('select_promotion', {
      event_category: 'ecommerce',
      event_label: 'Clicked on homepage banner',
      promotions: [{ name: banner.title, id: banner.id, creative_slot: 'banner' }],
    })

    eventGA4('select_promotion', {
      creative_slot: 'banner',
      promotion_name: banner.title,
      promotion_id: banner.id,
    })
  }

  return (
    <Box bg="gray.50" borderBottom="1px" borderColor="gray.200">
      <Container px={[0, , 12]} size="lg" display="flex" pt={[, , 10]} pb={[0, , 12]}>
        <Box
          h="100%"
          w="100%"
          overflow={['hidden', , 'initial']}
          paddingBottom={['calc(200 / 300 * 100%)', 'calc(400 / 1400 * 100%)']}
          position="relative"
          border="1px solid"
          borderColor="gray.200"
        >
          <Box position="absolute" h="100%" w="100%">
            {query.isFetching && !banners?.length ? (
              <Skeleton h="100%" w="100%" />
            ) : banners?.length === 1 ? (
              banners?.map(banner => {
                const image = (
                  <Image
                    src={imageProxy(banner.node.acf[imageKey]?.sourceUrl, { wp: true })}
                    objectFit="cover"
                    w="100%"
                    h="100%"
                  />
                )

                if (!banners[0].node?.acf?.link) return image

                const isLinkExternal = isMobileApp
                  ? banner.node.acf?.link?.startsWith?.('http') &&
                    !banner.node.acf?.link?.startsWith?.('https://thirstycamel.com.au') &&
                    !banner.node.acf?.link?.startsWith?.('https://www.thirstycamel.com.au')
                  : banner.node.acf?.isLinkExternal || banner.node.acf?.link?.startsWith?.('http')

                const url = isLinkExternal
                  ? banner.node.acf.link
                  : banner.node.acf.link
                      ?.replace('https://thirstycamel.com.au', '')
                      ?.replace('https://www.thirstycamel.com.au', '')

                return (
                  <Link href={url} isExternal={isLinkExternal} h="100%" w="100%" display="block">
                    {image}
                  </Link>
                )
              })
            ) : (
              <Carousel
                showDots
                arrows
                arrowLeft={<CarouselButton side="left" />}
                arrowRight={<CarouselButton side="right" />}
                infinite
                autoPlay={AUTOPLAY}
              >
                {banners.map(({ node: banner }) => {
                  const isLinkExternal = isMobileApp
                    ? banner.acf?.link?.startsWith?.('http') &&
                      !banner.acf?.link?.startsWith?.('https://thirstycamel.com.au') &&
                      !banner.acf?.link?.startsWith?.('https://www.thirstycamel.com.au')
                    : banner.acf?.isLinkExternal || banner.acf?.link?.startsWith?.('http')

                  const image = (
                    <Image
                      key={banner.title}
                      src={imageProxy(banner.acf[imageKey]?.sourceUrl, { wp: true })}
                      objectFit="cover"
                      w="100%"
                      h="100%"
                    />
                  )

                  if (!banner.acf?.link) return image

                  const url = isLinkExternal
                    ? banner.acf.link
                    : banner.acf.link
                        ?.replace('https://thirstycamel.com.au', '')
                        ?.replace('https://www.thirstycamel.com.au', '')

                  return (
                    <Link
                      href={url}
                      isExternal={isLinkExternal}
                      h="100%"
                      w="100%"
                      display="block"
                      onClick={() => handleClickBanner(banner)}
                    >
                      {image}
                    </Link>
                  )
                })}
              </Carousel>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export const HomeBanner = () => (cmsUseNew() ? <HomeBannerCore /> : <HomeBannerWordPress />)

export default HomeBanner
